import { acceptHMRUpdate, defineStore } from 'pinia';
import { getActiveUser } from '@/data/session/session-globals';
import values from 'lodash/values';
import { useAppsStore } from '@/stores/apps.store';
import { useStarsStore } from '@/stores/stars.store';
import { useHistoryStore } from '@/stores/history.store';
import {
    fetchFeatures,
    fetchFeature,
    updateFeature,
    createFeature,
    deleteFeature,
    shapeAnalyticsFeature,
} from '@/stores/features.utils';
import { createFrozenMap, appendKindIfNotProvided } from '@/vuex/utils';
import { isCrossApp } from '@/common/cross-app-countables/cross-app-countables.utils';
import { formatCrossAppDisplayName } from '@/stores/apps.utils.js';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';

interface FeatureIcon {
    type: string;
    [key: string]: unknown;
}

export interface FeatureRecord {
    id: string;
    name?: string;
    group?: unknown;
    appId?: string;
    appIds?: string[];
    kind?: string;
    dirty?: boolean;
    isCoreEvent?: boolean;
    icon?: FeatureIcon;
    eventPropertyConfigurations?: unknown;
    eventPropertyNameList?: unknown;
    [key: string]: unknown;
}

interface FeatureState {
    error: unknown;
    fetching: boolean;
    map: Record<string, FeatureRecord>;
}

interface FeatureWritePayload extends Partial<FeatureRecord> {
    shouldStartProcessing?: boolean;
}

interface ApiResponse<T> {
    data: T;
}

interface RecentFeature extends FeatureRecord {
    appIcon: unknown;
    appDisplayName: string | undefined;
    isStarred: boolean;
    icon: FeatureIcon;
    lastUsed: number;
}

let loadAllPromise: Promise<ApiResponse<FeatureRecord[]>> | null = null;

export const useFeaturesStore = defineStore('features', {
    state: (): FeatureState => ({
        error: {},
        fetching: false,
        map: {},
    }),
    getters: {
        list: (state) => values(state.map).filter((feature) => !isCrossApp(feature)),
        crossAppList: (state) => values(state.map).filter((feature) => isCrossApp(feature)),
        listIncludingCrossApp (): FeatureRecord[] {
            return [...this.list, ...this.crossAppList];
        },
        featureById: (state) => (id: string) => state.map[id],
        featuresByAppId (): (appId: string) => FeatureRecord[] {
            return (appId: string) => this.list.filter((feature) => feature.appId === appId);
        },
        featuresByAppIdIncludingCrossApp (): (appId: string) => FeatureRecord[] {
            return (appId: string) => this.listIncludingCrossApp.filter((feature) => {
                if (feature.appId === appId) return true;
                if (!feature.appIds?.length) return false;

                return feature.appIds.includes(appId);
            });
        },
        analyticsFeaturesListWithStars (): FeatureRecord[] {
            const deps = {
                isStarred: useStarsStore().getStarByItemId,
                getIconByAppId: useAppsStore().getIconByAppId,
            };

            return this.list.map((feature) => shapeAnalyticsFeature(feature, deps));
        },
        analyticsFeaturesListWithStarsIncludingCrossApp (): FeatureRecord[] {
            const deps = {
                isStarred: useStarsStore().getStarByItemId,
                getIconByAppId: useAppsStore().getIconByAppId,
            };

            return this.listIncludingCrossApp.map((feature) => shapeAnalyticsFeature(feature, deps));
        },
        recentFeaturesInReporting (state): RecentFeature[] {
            const appsStore = useAppsStore();
            const isStarred = useStarsStore().getStarByItemId;

            return useHistoryStore()
                .itemHistory(RECENTLY_SELECTED_HISTORY_TYPES.FEATURE_RECENTLY_SELECTED)
                .filter(({ id }) => state.map[id])
                .filter(({ id }) => !isCrossApp(state.map[id]))
                .map(({ id, timestamp }) => {
                    const feature = state.map[id];

                    return {
                        id: feature.id,
                        name: feature.name,
                        group: feature.group,
                        appId: feature.appId,
                        appIds: feature.appIds,
                        appIcon: feature.appId === undefined
                            ? undefined
                            : appsStore.getIconByAppId(feature.appId),
                        appDisplayName: formatCrossAppDisplayName(feature, appsStore.map),
                        kind: feature.kind,
                        dirty: feature.dirty,
                        isCoreEvent: feature.isCoreEvent,
                        isStarred: !!isStarred(feature.id),
                        icon: feature.icon || { type: 'feature' },
                        eventPropertyConfigurations: feature.eventPropertyConfigurations,
                        eventPropertyNameList: feature.eventPropertyNameList,
                        lastUsed: timestamp,
                    };
                });
        },
        recentFeaturesInReportingIncludingCrossApp (state): RecentFeature[] {
            const appsStore = useAppsStore();
            const isStarred = useStarsStore().getStarByItemId;

            return useHistoryStore()
                .itemHistory(RECENTLY_SELECTED_HISTORY_TYPES.FEATURE_RECENTLY_SELECTED)
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const feature = state.map[id];

                    return {
                        id: feature.id,
                        name: feature.name,
                        group: feature.group,
                        appId: feature.appId,
                        appIds: feature.appIds,
                        appIcon: feature.appId === undefined
                            ? undefined
                            : appsStore.getIconByAppId(feature.appId),
                        appDisplayName: formatCrossAppDisplayName(feature, appsStore.map),
                        kind: feature.kind,
                        dirty: feature.dirty,
                        isCoreEvent: feature.isCoreEvent,
                        isStarred: !!isStarred(feature.id),
                        icon: feature.icon || { type: 'feature' },
                        eventPropertyConfigurations: feature.eventPropertyConfigurations,
                        eventPropertyNameList: feature.eventPropertyNameList,
                        lastUsed: timestamp,
                    };
                });
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            const isSuperUser = getActiveUser()?.isSuper;

            this.fetching = true;
            loadAllPromise = fetchFeatures({ isSuperUser }) as Promise<ApiResponse<FeatureRecord[]>>;

            try {
                const { data: features } = await loadAllPromise;
                const featuresWithKind = features.map((feature) =>
                    appendKindIfNotProvided(feature, 'Feature') as FeatureRecord,
                );
                this.fetching = false;
                this.map = createFrozenMap(featuresWithKind) as Record<string, FeatureRecord>;
            } catch (error) {
                this.fetching = false;
                this.error = error;
                throw error;
            }
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string }) {
            if (!reFetch && this.featureById(id)) return;

            this.fetching = true;

            return fetchFeature(id)
                .then(({ data: feature }: ApiResponse<FeatureRecord>) => {
                    appendKindIfNotProvided(feature, 'Feature');
                    this.fetching = false;
                    this.map[feature.id] = Object.freeze(feature);
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async create ({ shouldStartProcessing = true, ...payload }: FeatureWritePayload = {}) {
            this.fetching = true;

            return createFeature(payload, shouldStartProcessing)
                .then(({ data: feature }: ApiResponse<FeatureRecord>) => {
                    this.fetching = false;
                    this.map[feature.id] = Object.freeze(feature);

                    return feature;
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async update ({ shouldStartProcessing = false, ...payload }: FeatureWritePayload = {}) {
            this.fetching = true;

            return updateFeature(payload, shouldStartProcessing)
                .then(({ data: feature }: ApiResponse<FeatureRecord>) => {
                    this.fetching = false;
                    this.map[feature.id] = Object.freeze(feature);
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async remove ({ id }: { id: string }) {
            this.fetching = true;

            return deleteFeature(id)
                .then(() => {
                    this.fetching = false;
                    delete this.map[id];
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useFeaturesStore, module.hot));
}
